<script lang="ts">
  // Ein Knoten im Explorer-Baum (docs/shell.md).
  //
  // Rendert sich selbst fuer seine Kinder -- deshalb eine eigene
  // Komponente statt drei verschachtelter {#each} in Explorer.svelte:
  // die Tiefe ist nicht festgelegt (Docker geht drei Ebenen tief,
  // Wetter zwei, Sensoren eine), und eine feste Schachtelung waere
  // genau die Begrenzung, die man spaeter wieder aufbricht.

  //
  // Anordnen (tree.svelte.ts, BaumOrdnen; was TreeNode davon braucht:
  // BaumOrdner in baum-knoten.svelte.ts): die Zeile gedrückt halten öffnet
  // den Sortiermodus ihrer Ebene, dann zieht man sie unter ihre Geschwister.
  // Gegriffen wird an der Zeile, nicht am Dreieck -- das klappt auch im
  // Modus auf und zu, damit man an tiefere Ebenen kommt.

  import GriffPunkte from "../GriffPunkte.svelte";
  import Icon from "../Icon.svelte";
  import { pressReorder, type PressReorderParams } from "../press-reorder";
  import TreeNode from "./TreeNode.svelte";
  import Kreuz from "../Kreuz.svelte";
  import { baumScharf, hatAktives, type BaumKnoten, type BaumOrdner } from "./baum-knoten.svelte";

  interface Props {
    knoten: BaumKnoten;
    tiefe: number;
    istAufgeklappt: (k: BaumKnoten) => boolean;
    klappe: (k: BaumKnoten) => void;
    ordnen: BaumOrdner;
    /** Kennung des Elternknotens ("" = oberste Ebene) */
    eltern: string;
    /** Kennungen der Ebene in ihrer jetzigen Reihenfolge */
    geschwister: string[];
    /** Der Weg zur aktiven Zeile, je Vorfahr (Ebene 0 bis tiefe-1):
     *  "durch" = er läuft senkrecht durch diese Zeile, "bogen" = er biegt
     *  hier in die Zeile ein (sie liegt auf dem Weg), "" = hier nicht. */
    linien?: Weg[];
  }
  type Weg = "durch" | "bogen" | "";
  let { knoten, tiefe, istAufgeklappt, klappe, ordnen, eltern, geschwister, linien = [] }: Props = $props();

  const sortiert = $derived(ordnen.ebene === eltern);
  const zug = $derived<PressReorderParams>({
    group: ordnen.gruppe(eltern),
    id: knoten.id,
    armed: sortiert,
    aus: geschwister.length < 2,
    griff: ".zeile",
    onActivate: () => (ordnen.ebene = eltern),
    onReorder: (_g, von, nach) => ordnen.verschiebe(eltern, geschwister, von, nach),
    liftClass: "anordnung-zieht",
  });

  const hatKinder = $derived(!!knoten.kinder?.length);
  const auf = $derived(hatKinder && istAufgeklappt(knoten));
  // Zugeklappt steht der Ast für alles, was in ihm liegt: ist dort eine
  // Zeile markiert (der aufgeklappte Rechner, die offene Aufgabe), trägt
  // er die Markierung -- wie bei den Zähl-Marken soll ein zugeklappter Ast
  // nichts verbergen.
  const aktiv = $derived(knoten.aktiv || (hatKinder && !auf && hatAktives(knoten.kinder ?? [])));
  const aktionen = $derived(knoten.aktionen ?? (knoten.aktion ? [knoten.aktion] : []));
  // Schmaler Baum: rechts ein Zeichen statt Text (Tim 19.10.2026: „Wenn der
  // Baum kleiner gezogen wird, dann sollten die Infos auf der rechten Seite
  // zu Symbolen werden"). Ein Zustand wird ein Punkt in seiner Farbe, eine
  // Zahl bleibt als Zahl („9 offen" → 9, „47 %" bleibt), alles andere
  // („gestern", „kein Chat", Uhrzeit „10:08", Datum „18.10.") fällt weg. Welche Form gilt, entscheidet die
  // Breite des Baums (@container baum), nicht das Fenster.
  const kurzZustand = $derived(knoten.status === "warn" || knoten.status === "bad" || knoten.status === "laeuft" ? knoten.status : null);
  const kurzZahl = $derived(kurzZustand ? null : (/^\d+(?:,\d+)?(?:\s?%)?(?=\s|$)/.exec(knoten.tail ?? "")?.[0] ?? null));
  // Einzug: schon die oberste Ebene eine Stufe unter dem Abschnittskopf
  // (Tim 23.10.2026: „Die Pfeile passen nicht zur Hierarchie -- bei den
  // Ordnern muss der Pfeil noch eingerückt werden"). Ohne die Stufe stand
  // der Pfeil eines Ordners genau unter dem Pfeil von „CHATS", sogar ein
  // paar Pixel links davon, und las sich wie eine Ebene höher.
  const einzug = $derived(`${(tiefe + 1) * 0.75}rem`);
  // Der Weg zur aktiven Zeile (Tim 26.10.2026: erst „dezente Linien, die
  // die Hierarchie zeigen", dann Äste zu jeder Zeile, dann „etwas mehr wie
  // in VS Code" und „die Linien nur dort, wo auch etwas aktiv ist. Den Rest
  // braucht es nicht" -- gewählt Variante D, „nur der Weg"): von jedem
  // Ordner auf dem Weg eine senkrechte Linie unter seinem Pfeil bis zur
  // nächsten Zeile des Wegs, dort ein sanfter Bogen hinein. Steht die
  // aktive Zeile in „Rückmeldungen", läuft der Weg von home-dash zu
  // Rückmeldungen und von dort zu ihr; steckt sie in einem zugeklappten
  // Ordner, endet er an ihm (er gilt dann als aktiv). Ohne aktive Zeile
  // keine Linie. Die Zeilen liegen flach untereinander, also zeichnet jede
  // ihr Stück selbst. Die Linie sitzt in der Mitte des Pfeils (Einzug +
  // halbe Breite 1,1rem); der Bogen reicht bis kurz vor den Pfeil eines
  // Ordners, bei einem Blatt bis kurz vor die Zeile.
  const linieLinks = (ebene: number) => (ebene + 1) * 0.75 + 0.55;
  const bogenBreite = $derived((tiefe + 1) * 0.75 + (hatKinder ? 0.15 : 0.75) - linieLinks(tiefe - 1));
  const traegtAktives = (k: BaumKnoten) => k.aktiv || hatAktives(k.kinder ?? []);
  // Was die Kinder vom Weg erben: wo er durch diese Zeile lief, läuft er
  // durch ihre Kinder weiter; biegt er hier ein, ist er für sie zu Ende.
  const erbe = $derived<Weg[]>(linien.map((w) => (w === "durch" ? "durch" : "")));
  const wegIndex = $derived((knoten.kinder ?? []).findIndex(traegtAktives));
  function linienFuer(i: number): Weg[] {
    return [...erbe, wegIndex < 0 || i > wegIndex ? "" : i < wegIndex ? "durch" : "bogen"];
  }
  // Wie viele Handgriffe beim Darüberfahren stehen (scharf: ✕ + der scharfe):
  // so viel Platz macht die Zeile rechts frei, der Name endet davor (Tim
  // 24.10.2026: „Die Symbole verschwimmen mit dem Text … dann besser den
  // Text abschneiden").
  const knoepfe = $derived(
    baumScharf.ist(knoten.id) ? 1 + aktionen.filter((a) => a.scharf).length : aktionen.length,
  );
</script>

<div class="zeile-zeile" class:sortiert class:mit-aktion={aktionen.length > 0} class:ist-scharf={aktionen.length > 0 && baumScharf.ist(knoten.id)} data-zeile use:pressReorder={zug}
  style:--knoepfe={knoepfe}>
  {#each linien as w, ebene (ebene)}
    {#if w === "durch"}
      <span class="linie" style:left="calc({linieLinks(ebene)}rem - 0.5px)" aria-hidden="true"></span>
    {:else if w === "bogen"}
      <span class="bogen" class:ordner={hatKinder} style:left="calc({linieLinks(ebene)}rem - 0.5px)" style:width="{bogenBreite}rem" aria-hidden="true"></span>
    {/if}
  {/each}
  <!-- Das Dreieck ist ein EIGENER Knopf: aufklappen und oeffnen sind
       zwei verschiedene Absichten. Wer eine Seite aufklappt, will nicht
       zwangslaeufig, dass sie sich oeffnet -- und umgekehrt. -->
  {#if hatKinder}
    <button
      type="button"
      class="dreieck"
      aria-expanded={auf}
      aria-label={auf ? "Zuklappen" : "Aufklappen"}
      style:margin-left={einzug}
      onclick={() => klappe(knoten)}
    >
      <Icon name="chevron-right" size={12} class={auf ? "rotate-90" : ""} />
    </button>
  {:else}
    <span class="dreieck leer" style:margin-left={einzug}></span>
  {/if}

  <button
    type="button"
    class="zeile"
    class:offen={knoten.offen}
    class:aktiv
    class:aus={knoten.status === "off"}
    aria-current={aktiv ? "page" : undefined}
    title={knoten.label}
    onclick={knoten.oeffnen}
    ondblclick={knoten.anheften}
  >
    {#if sortiert}
      <!-- im Modus: die sechs Punkte, wie an allem, was sich schieben lässt -->
      <span class="griff-platz"><GriffPunkte hoehe={10} class="text-accent" /></span>
    {:else if knoten.status && knoten.status !== "laeuft" && !knoten.ohnePunkt}
      <span class="punkt {knoten.status}"></span>
    {:else}
      <span class="punkt leer"></span>
    {/if}
    {#if knoten.icon}<Icon name={knoten.icon} size={13} class="baum-symbol flex-none opacity-70" />{/if}
    <span class="truncate">{knoten.label}</span>
    {#if knoten.tail}
      <span class="tail lang tabular-nums {kurzZustand ?? ''}">{knoten.tail}</span>
      {#if kurzZustand}<span class="tail kurz" aria-hidden="true"><i class="kurz-punkt {kurzZustand}"></i></span>
      {:else if kurzZahl}<span class="tail kurz tabular-nums" aria-hidden="true">{kurzZahl}</span>{/if}
    {/if}
    <!-- Offene Punkte: die SUMME, nicht nur die eigenen -- ein
         zugeklappter Ast soll nichts verbergen. Zugeklappt zeigt der
         Knoten also, was drinsteckt; aufgeklappt sieht man zusaetzlich,
         wo genau. -->
    {#if (knoten.summe ?? 0) > 0}
      <span class="punkte zaehl-marke" class:schwer={knoten.summeTon === "bad"}>{knoten.summe}</span>
    {/if}
    {#if knoten.fortschritt != null}
      <span class="lauf-fuellung" aria-hidden="true" style:width="{Math.max(0, Math.min(100, knoten.fortschritt))}%"></span>
    {/if}
  </button>
  {#if aktionen.length && !sortiert}
    {@const scharfDa = baumScharf.ist(knoten.id)}
    <!-- Handgriffe an der Zeile: beim Darüberfahren statt der Uhrzeit,
         scharf geschaltet bleibt der eine stehen (rot, daneben ✕). -->
    <span class="aktion">
      {#if scharfDa}
        <button type="button" class="aktion-knopf" title="Abbrechen" aria-label="Abbrechen" onclick={() => baumScharf.ab()}><Kreuz groesse={12} /></button>
      {/if}
      {#each aktionen as a (a.label)}
        {@const scharf = !!a.scharf && scharfDa}
        {#if !scharfDa || a.scharf}
          <button
            type="button"
            class="aktion-knopf"
            class:danger={scharf}
            title={scharf ? a.scharf : a.label}
            aria-label={scharf ? a.scharf : a.label}
            onclick={() => {
              if (!a.scharf || baumScharf.klick(knoten.id)) a.ausfuehren();
            }}
          >
            <Icon name={a.icon} size={13} />
          </button>
        {/if}
      {/each}
    </span>
  {/if}
</div>

{#if auf}
  {@const ids = (knoten.kinder ?? []).map((k) => k.id)}
  {#each knoten.kinder ?? [] as kind, i (kind.id)}
    <TreeNode knoten={kind} tiefe={tiefe + 1} {istAufgeklappt} {klappe} {ordnen} eltern={knoten.id} geschwister={ids} linien={linienFuer(i)} />
  {/each}
{/if}

<style>
  .zeile-zeile {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
  }
  /* Der Weg zur aktiven Zeile: senkrecht durch die Zeilen davor (volle
     Höhe samt 1px-Rand, damit die Stücke lückenlos anschließen), in die
     Zeile des Wegs ein sanfter Bogen bis auf halbe Höhe. */
  .linie,
  .bogen {
    position: absolute;
    top: 0;
    pointer-events: none;
    --baum-weg: color-mix(in srgb, var(--fg) 24%, transparent);
  }
  .linie {
    bottom: 0;
    width: 1px;
    background: var(--baum-weg);
  }
  .bogen {
    height: calc(50% + 0.5px);
    border-left: 1px solid var(--baum-weg);
    border-bottom: 1px solid var(--baum-weg);
    border-bottom-left-radius: 7px;
  }
  /* Vor dem Pfeil eines Ordners ist wenig Platz: kleinerer Bogen. */
  .bogen.ordner {
    border-bottom-left-radius: 4px;
  }
  /* Handgriff an der Zeile (BaumKnoten.aktion): erst beim Darüberfahren,
     dann statt der Zeitangabe; scharf geschaltet bleibt er sichtbar. */
  .aktion {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0.6rem;
    display: none;
    align-items: center;
    gap: 1px;
  }
  .zeile-zeile.mit-aktion:hover .aktion,
  .zeile-zeile.mit-aktion:focus-within .aktion,
  .zeile-zeile.ist-scharf .aktion {
    display: flex;
  }
  /* Die Handgriffe liegen über der Zeile; damit der Name nicht unter
     ihnen durchläuft, macht die Zeile rechts so viel Platz frei, wie sie
     brauchen (je Knopf 1,35rem + 1px Abstand, dazu ihr Rand 0,6rem minus
     Rand der Zeile 0,35rem und 0,3rem Luft), und der Zusatztext geht weg. */
  .zeile-zeile.mit-aktion:hover .zeile,
  .zeile-zeile.mit-aktion:focus-within .zeile,
  .zeile-zeile.ist-scharf .zeile {
    padding-right: calc(var(--knoepfe, 1) * (1.35rem + 1px) + 0.55rem);
  }
  .zeile-zeile.mit-aktion:hover .tail,
  .zeile-zeile.mit-aktion:focus-within .tail,
  .zeile-zeile.ist-scharf .tail {
    display: none;
  }
  .zeile-zeile.mit-aktion:hover .punkte,
  .zeile-zeile.mit-aktion:focus-within .punkte,
  .zeile-zeile.ist-scharf .punkte {
    margin-left: auto;
  }
  .aktion-knopf {
    display: grid;
    place-items: center;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 0.35rem;
    color: var(--fg-subtle);
  }
  .aktion-knopf:hover {
    color: var(--fg);
    background: color-mix(in srgb, var(--fg) 8%, transparent);
  }
  .aktion-knopf.danger {
    color: var(--danger);
    background: color-mix(in oklab, var(--danger) 14%, transparent);
  }
  /* Sortiermodus: greifbar, und die gezogene Zeile hebt sich ab */
  .zeile-zeile.sortiert .zeile {
    cursor: grab;
  }
  .zeile-zeile:global(.anordnung-zieht) {
    background: var(--bg-elev);
    border-radius: 0.4rem;
  }
  .griff-platz {
    display: grid;
    place-items: center;
    width: 0.4rem;
    flex: none;
  }
  .dreieck {
    display: grid;
    place-items: center;
    width: 1.1rem;
    flex: none;
    color: var(--fg-subtle);
  }
  .dreieck:hover {
    color: var(--fg);
  }
  .dreieck.leer {
    pointer-events: none;
  }
  .zeile {
    position: relative;
    /* Eigener Stapel: die Fortschritts-Füllung (z-index -1) liegt so ÜBER
       dem Grund der Zeile (offen, aktiv, hover), aber UNTER Symbol und
       Text. */
    isolation: isolate;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
    flex: 1;
    padding: 0.2rem 0.75rem 0.2rem 0.15rem;
    /* Winziger Abstand, damit zwei markierte Zeilen uebereinander
       nicht zu einem Block verschmelzen. */
    margin: 1px 0.35rem 1px 0;
    font-size: 0.84rem;
    /* EINE Schriftfarbe fuer jede Zeile. Bis 23.09. begann jede Zeile in
       --fg-muted, und erst ein offener Tab hob sie auf --fg -- neben den
       dunklen Zeilen las sich der Rest wie gesperrt (Tim: "nicht
       geoeffnete Seiten wirken wie ausgegraut"). Markiert wird ueber
       die Flaeche, nie ueber die Schrift. Mockup mit allen Varianten:
       docs/mockups/baum-markierung.html, gewaehlt Variante 03. */
    color: var(--fg);
    text-align: left;
    border-radius: 0.4rem;
  }
  .zeile:hover {
    background: var(--bg-elev);
  }
  /* Offen heisst: dieses Ding liegt schon als Tab bereit. Die Zeile
     markiert also Bestand, nicht Auswahl -- deshalb neutral grau
     (Akzentfarbe sah wie eine Warnung aus) und als abgerundete Pille
     statt kantiger Vollbreite (Tim 25.08.). Leise, weil die kraeftige
     Stufe der aktiven Seite gehoert. */
  .zeile.offen {
    background: color-mix(in srgb, var(--fg) 5%, transparent);
  }
  /* Aktiv: der Tab mit Fokus, die Seite, auf der man steht. Dieselbe
     Pille, nur lauter -- 5 % und 11 % liegen auf der Skala, die es
     schon gibt (Zebra 4,5 %, Hover 9 %, Auswahl 10-12 %). */
  .zeile.aktiv {
    background: color-mix(in srgb, var(--fg) 11%, transparent);
    font-weight: 600;
  }
  /* „Akzent kräftig" (app.css „Farbe"): die aktive Zeile trägt die Marke
     als Tönung und Kante. Die Schrift bleibt Textfarbe -- markiert wird
     weiter über die Fläche. Mit „Bereichsfarbe als Ort" ist die Marke
     die Farbe des Bereichs und liest sich nicht als Warnung. */
  :global(html[data-farbe-akzent="kraeftig"]) .zeile.aktiv {
    background: color-mix(in oklab, var(--marke) 15%, transparent);
    box-shadow: inset 2px 0 0 var(--marke);
  }
  /* Grau bleibt, was wirklich aus ist: offline Hosts, gestoppte
     Container, Pools ohne Zustand. Leiser, aber anklickbar -- wie
     VS Code ignorierte Dateien zeigt. Ring und Zusatztext tragen die
     Aussage, die Farbe bestaetigt sie nur. */
  .zeile.aus {
    color: var(--fg-muted);
  }
  .tail {
    margin-left: auto;
    padding-left: 0.4rem;
    font-size: 0.74rem;
    color: var(--fg-subtle);
    white-space: nowrap;
  }
  /* Farbe und Schrift kommen von .zaehl-marke (app.css) -- dieselbe Marke
     wie an der Leiste links. Gelb ist ein Handgriff, Rot bekommt nur, was
     wirklich brennt (Sicherheitsupdates, gescheiterte Aufgabe, kaputter
     Pool). */
  .punkte {
    margin-left: auto;
    flex: none;
  }
  .punkte.schwer {
    --stufe: var(--danger);
  }
  /* Steht eine Zahl daneben, darf der Zusatztext sie nicht wegdruecken. */
  .tail + .punkte {
    margin-left: 0.35rem;
  }
  .punkt {
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    flex: none;
  }
  .punkt.leer {
    background: transparent;
  }
  /* "Nur Abweichungen" (Tim 19.09.): gesund ist der Normalfall und
     bekommt KEINEN Punkt -- Farbe erscheint nur, wo etwas zu tun ist.
     Der Platz bleibt reserviert, damit Icons und Namen fluchten. */
  .punkt.ok {
    background: transparent;
  }
  .punkt.warn {
    background: var(--warning);
  }
  .punkt.bad {
    background: var(--danger);
  }
  /* Aus/offline: leerer Ring statt Fuellung -- sichtbar, aber ohne
     Alarmfarbe. */
  .punkt.off {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px var(--fg-subtle);
  }
  /* Der Zusatztext einer Abweichung ("kein SSH") traegt ihre Farbe. */
  .tail.warn {
    color: var(--warning);
  }
  .tail.kurz {
    display: none;
  }
  .kurz-punkt {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    display: inline-block;
    background: var(--fg-subtle);
  }
  .kurz-punkt.warn {
    background: var(--warning);
  }
  .kurz-punkt.bad {
    background: var(--danger);
  }
  .kurz-punkt.laeuft {
    background: var(--color-laeuft);
    animation: laeuft-puls 1.8s ease-in-out infinite;
  }
  @container baum (max-width: 240px) {
    .tail.lang {
      display: none;
    }
    .tail.kurz {
      display: inline-flex;
      align-items: center;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .kurz-punkt.laeuft {
      animation: none;
    }
  }
  .tail.bad {
    color: var(--danger);
  }
  /* Läuft gerade (lib/laeuft.svelte.ts): Akzent heißt „im Gang". Die
     Zeile füllt sich von links mit dem Fortschritt (Tim 15.10.2026: „das
     ganze Element von links nach rechts mit dem Fortschritt gefüllt"),
     die Zahl trägt die Farbe. Leicht getönt, damit die Schrift in jeder
     Lage lesbar bleibt -- ob der Tab zu, offen oder im Fokus ist; die
     Füllung legt sich über deren Grund, nicht an seine Stelle. */
  .tail.laeuft {
    color: var(--accent);
  }
  .lauf-fuellung {
    position: absolute;
    inset: 0 auto 0 0;
    z-index: -1;
    border-radius: 0.4rem 0 0 0.4rem;
    background: color-mix(in oklab, var(--accent) 18%, transparent);
    pointer-events: none;
    transition: width 0.6s ease;
  }
  /* Mit kräftigem Akzent trägt die aktive Zeile links eine Kante (siehe
     unten) -- die Füllung beginnt dahinter, sonst verdeckte sie sie. */
  :global(html[data-farbe-akzent="kraeftig"]) .zeile.aktiv .lauf-fuellung {
    left: 2px;
    border-radius: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .lauf-fuellung {
      transition: none;
    }
  }
</style>
